import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, LogOut, Clock } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

/**
 * Modal que aparece quando a sessão do usuário expira
 * Mostra mensagem contextual baseada no motivo da expiração
 */
export function SessionExpiredModal({ isOpen, onConfirm, reason }) {
  useBodyScrollLock(isOpen);
  if (!isOpen) return null;

  // Mensagens contextuais baseadas no motivo da expiração
  const messages = {
    'token_refresh_failed': {
      title: 'Sessão Expirada',
      description: 'Sua sessão expirou por inatividade. Por favor, faça login novamente para continuar.',
      icon: Clock,
      color: 'amber'
    },
    'already_expired': {
      title: 'Sessão Expirada',
      description: 'Sua sessão expirou. Faça login novamente para acessar o sistema.',
      icon: Clock,
      color: 'amber'
    },
    'expired_during_use': {
      title: 'Sessão Expirada',
      description: 'Sua sessão expirou durante o uso. Faça login novamente para continuar.',
      icon: Clock,
      color: 'amber'
    },
    'validation_error': {
      title: 'Erro de Validação',
      description: 'Não foi possível validar sua sessão. Por favor, faça login novamente.',
      icon: AlertCircle,
      color: 'red'
    },
    'validation_renewal_failed': {
      title: 'Erro ao Renovar Sessão',
      description: 'Não foi possível renovar sua sessão automaticamente. Faça login novamente.',
      icon: AlertCircle,
      color: 'red'
    },
    'auto_renewal_failed': {
      title: 'Erro ao Renovar Sessão',
      description: 'Falha ao renovar sessão automaticamente. Por favor, faça login novamente.',
      icon: AlertCircle,
      color: 'red'
    },
    'check_expiration_error': {
      title: 'Erro de Conexão',
      description: 'Não foi possível verificar o status da sua sessão. Faça login novamente.',
      icon: AlertCircle,
      color: 'red'
    }
  };

  // Mensagem padrão caso o motivo não seja reconhecido
  const defaultMessage = {
    title: 'Sessão Expirada',
    description: 'Sua sessão expirou. Por favor, faça login novamente.',
    icon: Clock,
    color: 'amber'
  };

  const message = messages[reason] || defaultMessage;
  const Icon = message.icon;

  // Cores baseadas no tipo de mensagem
  const colorClasses = {
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'text-amber-600',
      title: 'text-amber-900',
      description: 'text-amber-700',
      button: 'bg-amber-600 hover:bg-amber-700'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-600',
      title: 'text-red-900',
      description: 'text-red-700',
      button: 'bg-red-600 hover:bg-red-700'
    }
  };

  const colors = colorClasses[message.color];

  // Portal: escapa do fluxo normal da página, evitando que containers com
  // margin/spacing (ex.: space-y-*) desloquem o overlay fixed.
  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        {/* Modal */}
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-in fade-in zoom-in duration-200">
          {/* Header com ícone */}
          <div className={`${colors.bg} ${colors.border} border-b px-6 py-8 rounded-t-lg`}>
            <div className="flex flex-col items-center text-center">
              <div className={`${colors.bg} p-3 rounded-full mb-4`}>
                <Icon className={`w-12 h-12 ${colors.icon}`} />
              </div>
              <h2 className={`text-2xl font-bold ${colors.title}`}>
                {message.title}
              </h2>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="px-6 py-6">
            <p className={`text-center ${colors.description} text-base leading-relaxed`}>
              {message.description}
            </p>

            {/* Informação adicional */}
            <div className={`mt-4 ${colors.bg} ${colors.border} border rounded-lg p-4`}>
              <p className={`text-sm ${colors.description} text-center`}>
                💡 <strong>Dica:</strong> Para evitar perder seu trabalho, salve suas alterações regularmente.
              </p>
            </div>
          </div>

          {/* Footer com botão */}
          <div className="px-6 pb-6">
            <Button
              onClick={onConfirm}
              className={`w-full ${colors.button} text-white font-semibold py-3 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2`}
            >
              <LogOut className="w-5 h-5" />
              Fazer Login Novamente
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
